import React, { useState, useEffect } from 'react';
import './HeaderNavBar.css';
import {
  Users,
  ShieldCheck,
  ShoppingCart,
  FileText,
  LogOut,
  User,
  Menu,
  Settings,
  BarChart3,
  Building2,
  X,
} from 'lucide-react';
import { WarehouseOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LogoutButton } from './LogoutButton';
import logo from '../assets/logo.svg';

export const HeaderNavBar: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationSections = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      rolesAllowed: ['practitioner'],
      path: '/dashboard',
    },
    //   { id: "providers", label: "Providers", icon: UserPlus, rolesAllowed:["admin","manufacturer","practitioner","insurance"] , path: '/providers'},
    {
      id: 'demographics',
      label: 'Demographics',
      icon: Users,
      rolesAllowed: ['practitioner'],
      path: '/demographics',
    },
    {
      id: 'product-management',
      label: 'Product Management',
      icon: WarehouseOutlined,
      rolesAllowed: ['admin', 'manufacturer'],
      rolesRequiringExplicitPermission: ['admin'], // Admin needs explicit permission, manufacturer can use role
      path: '/product-management',
    },
    // { id: "inventory-dashboard", label: "Inventory Dashboard", icon: Users, rolesAllowed:["admin"] , path: '/inventory-dashboard'},
    {
      id: 'orders',
      label: 'Orders',
      icon: ShoppingCart,
      rolesAllowed: ['manufacturer', 'practitioner'],
      path: '/orders',
    },
    {
      id: 'insurance',
      label: 'Insurance',
      icon: ShieldCheck,
      rolesAllowed: ['insurance'],
      path: '/insurance',
    },
    // { id: "verifications", label: "Verifications", icon: CheckCircle, rolesAllowed:["practitioner","insurance"] , path: '/verifications'},
    {
      id: 'invoices',
      label: 'Invoicing',
      icon: FileText,
      rolesAllowed: ['manufacturer', 'practitioner'],
      path: '/invoices',
    },
    {
      id: 'user-management',
      label: 'User Management',
      icon: User,
      rolesAllowed: ['admin'],
      path: '/user-management',
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      rolesAllowed: [
        'admin',
        'manufacturer',
        'practitioner',
        'insurance',
        'insurance_verifier',
      ],
      path: '/settings',
    },
  ];

  const [activeSection, setActiveSection] = useState(() => {
    // Set initial active section based on current location
    const currentPath = location.pathname;
    const currentSection = navigationSections.find(section => section.path === currentPath);
    if (currentSection) {
      return currentSection.id;
    }
    // Fallback to role-based defaults
    if (user?.role === 'admin') {
      return 'user-management';
    }
    return 'dashboard';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getNavItems = () => {
    if (!user || !user.permissions) return [];

    // Map permission names to section IDs
    const permissionToSectionMap: Record<string, string> = {
      user_management: 'user-management',
      settings: 'settings',
      product_management: 'product-management',
      demographics: 'demographics',
      orders: 'orders',
      invoices: 'invoices',
      insurance: 'insurance',
    };

    return navigationSections.filter(section => {
      // Check if user has permission for this section
      const hasPermission = user.permissions.some(permission => {
        const mappedSectionId = permissionToSectionMap[permission];
        return mappedSectionId === section.id;
      });

      // Check if this section has role-specific permission requirements
      if (section.rolesRequiringExplicitPermission && section.rolesRequiringExplicitPermission.includes(user.role)) {
        // This role requires explicit permission (e.g., admin for Product Management)
        return hasPermission;
      }

      // For other roles, check role-based access as fallback
      const hasRoleAccess = section.rolesAllowed.includes(user.role);

      return hasPermission || hasRoleAccess;
    });
    // switch (user?.role) {
    //   case 'admin':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('admin'));
    //   case 'manufacturer':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('manufacturer'));
    //   case 'practitioner':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('practitioner'));
    //   case 'insurance':
    //     return navigationSections.filter(section => section.rolesAllowed.includes('insurance'));
    //   default:
    //     return navigationSections.filter(section => section.rolesAllowed.includes('admin'));
    // }
  };

  const navItems = getNavItems();

  const handleNavigation = (path: string, sectionId: string) => {
    setActiveSection(sectionId);
    navigate(path);
  };

  // Remove handleLogout function as we'll use LogoutButton component

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Set active section based on current location
  useEffect(() => {
    const currentPath = location.pathname;
    const currentSection = navigationSections.find(section => section.path === currentPath);
    if (currentSection) {
      setActiveSection(currentSection.id);
    } else {
      // If no matching section found, set default based on user role
      if (user?.role === 'admin') {
        setActiveSection('user-management');
      } else {
        setActiveSection('dashboard');
      }
    }
  }, [location.pathname, user?.role]);

  return (
    <header className="header-navbar">
      {/* Desktop Header */}
      <div className="header-desktop">
        <div className="header-brand">
          <img src={logo} alt="MPM Medical" className="header-logo" />
        </div>

        <nav className="header-navigation">
          {navItems.map(section => (
            <button
              key={section.id}
              className={`header-nav-item ${
                activeSection === section.id ? 'active' : ''
              }`}
              onClick={() => handleNavigation(section.path, section.id)}
            >
              <section.icon className="header-nav-icon" />
              <span className="header-nav-label">{section.label}</span>
            </button>
          ))}
        </nav>

        <div className="header-user-section">
          <div className="user-profile">
            <div className="user-avatar">
              <User className="avatar-icon" />
            </div>
            <div className="user-info">
              <span className="user-name">{user?.name || 'User'}</span>
              <span className="user-role">{user?.role || 'Role'}</span>
            </div>
          </div>
          <LogoutButton className="logout-button">
            <LogOut className="logout-icon" />
          </LogoutButton>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <Menu className="mobile-menu-icon" />
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-brand">
            <Building2 className="mobile-brand-icon" />
            <span className="mobile-brand-title app-title">mpm MEDICAL</span>
          </div>
          <button
            className="mobile-menu-close"
            onClick={toggleMobileMenu}
            aria-label="Close mobile menu"
          >
            <X className="close-icon" />
          </button>
        </div>

        <nav className="mobile-navigation">
          {navItems.map(section => (
            <div
              key={section.id}
              className={`mobile-nav-item ${
                activeSection === section.id ? 'active' : ''
              }`}
              onClick={() => {
                toggleMobileMenu();
                handleNavigation(section.path, section.id);
              }}
              style={{ cursor: 'pointer' }}
            >
              <section.icon className="mobile-nav-icon" />
              <span className="mobile-nav-label nav-item">{section.label}</span>
            </div>
          ))}
        </nav>

        <div className="mobile-user-section">
          <div className="mobile-user-profile">
            <div className="mobile-user-avatar">
              <User className="mobile-user-avatar-icon" />
            </div>
            <div className="mobile-user-info">
              <p className="mobile-user-name text-subtitle">{user?.name}</p>
              <p className="mobile-user-role text-paragraph">{user?.role}</p>
            </div>
          </div>

          <LogoutButton className="mobile-logout-button">
            <LogOut className="mobile-logout-icon" />
            <span>Logout</span>
          </LogoutButton>
        </div>
      </div>
    </header>
  );
};
